import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Eye, Save, FileText, Paperclip, Upload, X } from 'lucide-react';
import { emailService, EmailTemplate, EmailAttachment } from 'api/services/email.service';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import { ErrorMessage } from 'components/Common/ErrorMessage';

const blank: EmailTemplate = { name: '', subject: '', htmlBody: '', textBody: '', category: '' };

const EmailTemplates: React.FC = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<EmailTemplate | null>(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<{ subject: string; htmlBody: string; unresolved: string[] } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);

  /**
   * Upload, then append what the SERVER returned.
   *
   * The response is one element of template.attachments, so the shape is never
   * assembled here — a field renamed on the server would otherwise become a
   * template that silently attaches nothing.
   *
   * The file is stored immediately; the template is not saved until the user saves
   * it. So an abandoned edit leaves an orphan object in the bucket rather than a
   * document that starts going out with the next campaign.
   */
  const onPickAttachment = async (file: File | null) => {
    if (!file || !editing) return;
    setAttachError(null);
    setUploading(true);
    try {
      const res = await emailService.uploadTemplateAttachment(file);
      if (res.success && res.data) {
        setEditing({ ...editing, attachments: [...(editing.attachments || []), res.data] });
      } else {
        setAttachError(res.error || 'Upload failed');
      }
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (idx: number) => {
    if (!editing) return;
    // Removed from the template only. The object stays in the bucket: another
    // template may reference the same URL, and deleting it here would break that
    // one's next send.
    const next = [...(editing.attachments || [])];
    next.splice(idx, 1);
    setEditing({ ...editing, attachments: next });
  };

  const prettyBytes = (n?: number) =>
    !n ? '' : n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;

  const load = async () => {
    setLoading(true);
    const res = await emailService.listTemplates();
    if (res.success) setTemplates(res.data || []); else setError(res.message || 'Failed to load');
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    setError(null);
    if (!editing.name.trim() || !editing.subject.trim()) return setError('Name and subject are required');
    setSaving(true);
    const res = editing.id
      ? await emailService.updateTemplate(editing.id, editing)
      : await emailService.createTemplate(editing);
    setSaving(false);
    if (res.success) { setEditing(null); setPreview(null); await load(); }
    else setError(res.message || 'Failed to save template');
  };

  const remove = async (id: string) => {
    const res = await emailService.deleteTemplate(id);
    if (res.success) await load(); else setError(res.message || 'Failed to delete');
  };

  const doPreview = async () => {
    if (!editing?.id) { setError('Save the template first to preview it'); return; }
    const sample: Record<string, string> = {};
    (editing.variables || []).forEach((v) => { sample[v] = `[${v}]`; });
    const res = await emailService.previewTemplate(editing.id, sample);
    if (res.success && res.data) setPreview(res.data);
    else setError(res.message || 'Preview failed');
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <button onClick={() => navigate('/email/campaigns')}
        className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 hover:text-indigo-600">
        <ArrowLeft className="w-4 h-4" /> Back to campaigns
      </button>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="w-6 h-6 text-indigo-600" />
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Email Templates</h1>
        </div>
        {!editing && (
          <button onClick={() => { setEditing({ ...blank }); setPreview(null); }}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg">
            <Plus className="w-4 h-4" /> New Template
          </button>
        )}
      </div>

      {error && <ErrorMessage message={error} />}

      {editing ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <FieldInput label="Name *" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
              <FieldInput label="Category" value={editing.category || ''} onChange={(v) => setEditing({ ...editing, category: v })} />
            </div>
            <FieldInput label="Subject *" value={editing.subject} onChange={(v) => setEditing({ ...editing, subject: v })} placeholder="Hi {{first_name}}, a quick update" />
            <FieldArea label="HTML body" rows={8} value={editing.htmlBody || ''} onChange={(v) => setEditing({ ...editing, htmlBody: v })} placeholder="<p>Hello {{first_name}}…</p>" />
            <FieldArea label="Plain-text body (recommended for deliverability)" rows={4} value={editing.textBody || ''} onChange={(v) => setEditing({ ...editing, textBody: v })} />

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Paperclip className="w-4 h-4" /> Attachments
                </label>
                <label className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border cursor-pointer
                  ${uploading ? 'opacity-50 cursor-not-allowed border-gray-300' : 'border-blue-600 text-blue-700 hover:bg-blue-50'}`}>
                  <Upload className="w-4 h-4" />
                  {uploading ? 'Uploading…' : 'Add file'}
                  <input
                    type="file"
                    className="hidden"
                    disabled={uploading}
                    accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.csv,.txt,.doc,.docx,.xls,.xlsx"
                    onChange={(e) => { onPickAttachment(e.target.files?.[0] || null); e.target.value = ''; }}
                  />
                </label>
              </div>

              <p className="text-xs text-gray-500">
                Sent with every message from this template. PDF, image, CSV, text, Word or Excel, up to 8 MB total —
                attachments are sent to everyone in a campaign, so this is not the place for anything personal.
              </p>

              {attachError && <p className="text-xs text-red-600">{attachError}</p>}

              {(editing.attachments || []).length === 0 ? (
                <p className="text-xs text-gray-400">No attachments.</p>
              ) : (
                <ul className="border border-gray-200 rounded-lg divide-y">
                  {(editing.attachments || []).map((a: EmailAttachment, i: number) => (
                    <li key={a.url + i} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span className="flex items-center gap-2 min-w-0">
                        <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="truncate">{a.filename}</span>
                        <span className="text-xs text-gray-400 flex-shrink-0">{prettyBytes(a.sizeBytes)}</span>
                      </span>
                      <button
                        type="button"
                        onClick={() => removeAttachment(i)}
                        className="text-gray-400 hover:text-red-600 flex-shrink-0"
                        title="Remove from this template (the file itself is kept)"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {editing.variables && editing.variables.length > 0 && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Detected variables: {editing.variables.map((v) => <code key={v} className="mx-1 px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">{`{{${v}}}`}</code>)}
              </div>
            )}
            <div className="flex justify-between pt-2">
              <button onClick={() => { setEditing(null); setPreview(null); }} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:underline">Cancel</button>
              <div className="flex gap-2">
                <button onClick={doPreview} className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                  <Eye className="w-4 h-4" /> Preview
                </button>
                <button onClick={save} disabled={saving} className="tl-btn tl-btn-primary tl-btn-sm disabled:opacity-50">
                  <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">Preview</h3>
            {preview ? (
              <div className="space-y-3">
                <div className="text-sm"><span className="text-gray-500">Subject:</span> <strong className="text-gray-900 dark:text-white">{preview.subject}</strong></div>
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-900 max-h-96 overflow-auto"
                  dangerouslySetInnerHTML={{ __html: preview.htmlBody || '<em>(no HTML body)</em>' }} />
                {preview.unresolved.length > 0 && (
                  <div className="text-xs text-orange-500">Unfilled in sample: {preview.unresolved.join(', ')}</div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-400">Save the template, then Preview to render it with sample values.</p>
            )}
          </div>
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-16 text-gray-500 dark:text-gray-400">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>No templates yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((t) => (
            <div key={t.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">{t.name}</div>
                  {t.category && <div className="text-xs text-gray-400">{t.category}</div>}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => { setEditing(t); setPreview(null); }} className="p-1.5 text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded"><Eye className="w-4 h-4" /></button>
                  <button onClick={() => t.id && remove(t.id)} className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300 mt-2 line-clamp-1">{t.subject}</div>
              {t.variables && t.variables.length > 0 && (
                <div className="text-xs text-gray-400 mt-2">{t.variables.length} variable(s)</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const cls = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none';
const FieldInput: React.FC<{ label: string; value: string; onChange: (v: string) => void; placeholder?: string }> = ({ label, value, onChange, placeholder }) => (
  <label className="block"><span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</span>
    <input value={value} onChange={(e) => onChange(e.target.value)} className={cls} placeholder={placeholder} /></label>
);
const FieldArea: React.FC<{ label: string; value: string; onChange: (v: string) => void; rows: number; placeholder?: string }> = ({ label, value, onChange, rows, placeholder }) => (
  <label className="block"><span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</span>
    <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={rows} className={`${cls} font-mono text-xs`} placeholder={placeholder} /></label>
);

export default EmailTemplates;
